// COS-MODES (docs/design/2026-10-30-chief-of-staff-model.md; founder-approved 2026-10-30): the
// Chief of Staff's two modes under one company switch, the per-head level "Who may direct my
// agents", the server's routing verdict for an agent directing another person's agent, and Take
// back on the inbox item that tells the head.
//
//   Solo: every agent is directed directly (the card is the trail).
//   Team: the target agent's chain is walked to the nearest person; the CEO → direct; another
//         person H → H's level: Hands-off (direct), Tell me (act and inform, Take back), Ask me
//         first (for the ticked action types: a `task_change` approval to H with a timeout that
//         proceeds or escalates to the CEO). Status reads are always direct and silent, urgent is
//         always act-and-inform, configuration is always refused.
import { z } from "zod";
import { Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import {
  AgentDirectionSetting,
  CosMode,
  DirectionAction,
  DirectionLevel,
  DirectionPerson,
  DirectionVerdict,
} from "./direction";
import { defineRoute } from "./route";
import { TaskDetail } from "./work";

const base = "/v1/companies/{co}";

/** Setup › Workforce › Chief of Staff › Mode. */
export const CosModeView = z
  .object({
    mode: CosMode,
    department_level_default: DirectionLevel,
    /** The default binds every head (their own level is not used). */
    locked: z.boolean(),
    /** The person the Chief of Staff works for (null: nobody leads the chart yet). */
    ceo: DirectionPerson.nullable(),
    /** People other than the CEO who lead agents, with their level as it applies. */
    heads: z.array(DirectionPerson.extend({ agents: z.int().min(0), level: DirectionLevel })),
    /** Solo with heads: the banner suggesting Team (never switched automatically). */
    suggest_team: z.boolean(),
  })
  .meta({ id: "CosModeView" });
export type CosModeView = z.infer<typeof CosModeView>;

export const CosModePut = z
  .strictObject({
    mode: CosMode,
    department_level_default: DirectionLevel,
    locked: z.boolean(),
  })
  .meta({ id: "CosModePut" });
export type CosModePut = z.infer<typeof CosModePut>;

/** The caller's own card "Who may direct my agents". */
export const AgentDirectionView = z
  .object({
    mode: CosMode,
    /** The setting as it applies (the company default where the person set none). */
    setting: AgentDirectionSetting,
    /** The company locked the level: the person's own is not used. */
    locked: z.boolean(),
    company_default: DirectionLevel,
    /** Agents whose chain reaches a person first at the caller. */
    agents: z.int().min(0),
  })
  .meta({ id: "AgentDirectionView" });
export type AgentDirectionView = z.infer<typeof AgentDirectionView>;

export const DirectionCheck = z
  .strictObject({
    /** The agent addressed: its seat, or its membership (one of them). */
    target_seat_id: Uuid.optional(),
    target_membership_id: Uuid.optional(),
    action: DirectionAction,
  })
  .meta({ id: "DirectionCheck" });
export type DirectionCheck = z.infer<typeof DirectionCheck>;

export const TaskTakeBack = z
  .strictObject({
    /** The `task.agent_directed` event the inbox item came from. */
    event_id: Uuid,
  })
  .meta({ id: "TaskTakeBack" });

export const cosModeRoutes = {
  "chiefOfStaff.mode": defineRoute({
    id: "chiefOfStaff.mode",
    feature: "agents",
    tag: "chief-of-staff",
    method: "get",
    path: `${base}/chief-of-staff/mode`,
    summary:
      "The Chief of Staff's mode (Solo or Team), the default level and the heads (a manager, or the CEO)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: CosModeView },
    errors: ["forbidden"],
    emits: [],
  }),
  "chiefOfStaff.setMode": defineRoute({
    id: "chiefOfStaff.setMode",
    feature: "agents",
    tag: "chief-of-staff",
    method: "put",
    path: `${base}/chief-of-staff/mode`,
    summary:
      "Switch Solo / Team, set the department default level and lock it (a manager, or the CEO the Chief of Staff works for)",
    access: "member",
    params: CompanyParams,
    body: CosModePut,
    response: { status: 200, schema: CosModeView },
    errors: ["forbidden"],
    emits: [
      "company.updated",
      // the switch: each current department head is told once
      "chief_of_staff.mode_changed",
      // Team → Solo: the waiting Ask-me-first requests proceed
      "approval.expired",
      "task.assigned",
      "task.unassigned",
      "task.handed_off",
      "task.priority_changed",
      "task.due_changed",
      "task.agent_directed",
      // a new brief to an agent already on the task: posted on the card, the agent woken
      "message.created",
      "wakeup.requested",
    ],
  }),
  "agentDirection.get": defineRoute({
    id: "agentDirection.get",
    feature: "agents",
    tag: "chief-of-staff",
    method: "get",
    path: `${base}/agent-direction`,
    summary: "My card: who may direct my agents (Hands-off, Tell me, Ask me first)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: AgentDirectionView },
    errors: ["forbidden"],
    emits: [],
  }),
  "agentDirection.put": defineRoute({
    id: "agentDirection.put",
    feature: "agents",
    tag: "chief-of-staff",
    method: "put",
    path: `${base}/agent-direction`,
    summary: "Set who may direct my agents",
    access: "member",
    params: CompanyParams,
    body: AgentDirectionSetting,
    response: { status: 200, schema: AgentDirectionView },
    errors: ["forbidden"],
    emits: ["membership.updated"],
  }),
  "agentDirection.check": defineRoute({
    id: "agentDirection.check",
    feature: "agents",
    tag: "chief-of-staff",
    method: "post",
    path: `${base}/agent-direction/check`,
    summary: "How the system routes an agent's action on another agent (nothing is written)",
    access: "member",
    params: CompanyParams,
    body: DirectionCheck,
    safe: true,
    response: { status: 200, schema: DirectionVerdict },
    errors: ["not_found"],
    emits: [],
  }),
  "tasks.takeBack": defineRoute({
    id: "tasks.takeBack",
    feature: "core",
    tag: "tasks",
    method: "post",
    path: `${base}/tasks/{id}/take-back`,
    summary:
      "Take back what an agent directed onto my agent (reassign to me, or restore the field)",
    access: "member",
    params: CompanyItemParams,
    body: TaskTakeBack,
    response: { status: 200, schema: TaskDetail },
    errors: ["forbidden", "not_found", "conflict"],
    emits: [
      "task.taken_back",
      "task.assigned",
      "task.unassigned",
      "task.handed_off",
      "task.priority_changed",
      "task.due_changed",
      "run.cancelled",
      "budget.settled",
      "message.created",
    ],
  }),
} as const;
